import { cx } from '@twind/core';
import { useState, useRef, useEffect, useCallback } from 'react';

import RightChevronIcon from './icons/RightChevronIcon';
import OstroDynamicContent from './OstroDynamicContent';

export interface DropdownMenuItemInterface {
  name: string;
  description: string;
  id: string;
  underlineColor?: string;
  displayName?: string;
}

export interface DropdownMenuItemProps {
  item: DropdownMenuItemInterface;
  onItemSelect?: (item: DropdownMenuItemInterface) => void;
  className?: string;
  underlineColor?: string;
  style?: React.CSSProperties;
  isActive?: boolean;
}

interface DropdownMenuButtonProps {
  title: string;
  className?: string;
  isOpen: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  selectedItem?: DropdownMenuItemInterface;
}

interface DropdownMenuProps {
  id: string;
  title: string;
  items: DropdownMenuItemInterface[];
  onItemSelect: (item: DropdownMenuItemInterface) => void;
  className?: string;
  disclaimer?: string;
  menuItemComponent?: React.ComponentType<DropdownMenuItemProps>;
  onOpen?: () => void;
  disclaimerEnabled?: boolean;
  buttonClassName?: string;
  selectedItem?: DropdownMenuItemInterface;
  buttonComponent?: React.ComponentType<DropdownMenuButtonProps>;
  containerRef?: React.RefObject<HTMLElement>;
}

const DropdownMenuItem = ({ item, onItemSelect, className }: DropdownMenuItemProps) => {
  return (
    <li
      role="menuitem"
      className={cx('~(group flex items-start justify-between px-4 py-3 cursor-pointer text-gray-700)', className)}
      onClick={() => onItemSelect?.(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onItemSelect?.(item);
        }
      }}
      tabIndex={onItemSelect ? 0 : -1}
    >
      <OstroDynamicContent content={item.name} />
      <OstroDynamicContent content={item.description} />
    </li>
  );
};

const DropdownMenuButton = ({ title, className, isOpen, onClick, selectedItem }: DropdownMenuButtonProps) => {
  return (
    <button
      type="button"
      aria-haspopup="true"
      aria-expanded={isOpen}
      onClick={onClick}
      className={cx(
        '~(w-full text-typography-heading text-sm leading-[14px] flex items-center justify-between px-3 py-2)',
        '~(border border-gray-300 rounded-lg focus:(outline-none ring-2 ring-blue-500 ring-offset-2))',
        className
      )}
    >
      <OstroDynamicContent
        content={selectedItem?.displayName ?? title}
        className="line-clamp-2 [&_p]:mb-0 w-full"
      />
      <RightChevronIcon className="w-[10px] h-[11px] ml-1" />
    </button>
  );
};

const DropdownMenu = ({
  id,
  title,
  items,
  onItemSelect,
  className,
  disclaimer,
  menuItemComponent,
  onOpen,
  disclaimerEnabled,
  buttonClassName,
  selectedItem,
  buttonComponent,
  containerRef,
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: '50%', transform: 'translateX(-50%)' });
  const dropdownRef = useRef<HTMLDivElement>(null);

  const calculateMenuPosition = useCallback(() => {
    if (!containerRef?.current || !dropdownRef.current) {
      setMenuPosition({ left: '50%', transform: 'translateX(-50%)' });
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();

    const containerCenter = containerRect.left + containerRect.width / 2;

    const dropdownLeft = dropdownRect.left;
    const relativeLeft = containerCenter - dropdownLeft;

    setMenuPosition({
      left: `${relativeLeft}px`,
      transform: 'translateX(-50%)',
    });
  }, [containerRef]);

  useEffect(() => {
    if (isOpen && containerRef?.current) {
      calculateMenuPosition();

      const handleResize = () => calculateMenuPosition();
      window.addEventListener('resize', handleResize);

      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen, containerRef, calculateMenuPosition]);

  const toggleDropdown = (event: React.MouseEvent<HTMLButtonElement>) => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (newIsOpen && onOpen) {
      onOpen();
    }
    if (!newIsOpen) {
      event.currentTarget.blur();
    }
  };

  const MenuItemComponent = menuItemComponent || DropdownMenuItem;
  const ButtonComponent = buttonComponent || DropdownMenuButton;

  return (
    <div
      ref={dropdownRef}
      id={id}
      className={cx('ostro-dropdown-menu-container relative', className)}
    >
      <ButtonComponent
        title={title}
        className={buttonClassName}
        isOpen={isOpen}
        onClick={toggleDropdown}
        selectedItem={selectedItem}
      />

      {isOpen && (
        <div
          role="menu"
          className={cx(
            'absolute mt-2 bg-white rounded-md shadow-[0px_6px_24px_0px_rgba(0,0,0,0.10)] z-50',
            'w-[315px]',
            'max-w-[calc(100vw-30px)]'
          )}
          style={menuPosition}
        >
          <ul className="max-h-100 overflow-y-auto">
            {items.map((item, index) => (
              <MenuItemComponent
                key={index}
                item={item}
                onItemSelect={onItemSelect}
                underlineColor={item.underlineColor}
                isActive={item.id === selectedItem?.id}
              />
            ))}
            {disclaimerEnabled && disclaimer && (
              <MenuItemComponent
                item={{ name: '', description: disclaimer, id: '' }}
                className="cursor-default pointer-events-none"
              />
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
